import { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { Provider } from '@dataTypes/provider.types'
import { getAllStaffs } from '@services/api/provider.api'
import { MOCK_PROVIDERS } from '@assets/mockdata/Provider/mockProviders.data'
import { getEnv } from '@utils/env'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { StaffWeek, toStaffWeek } from '../staffShifts.util'

interface UseStaffShiftsResult {
  staffs: Provider[]
  staffWeeks: StaffWeek[]
  loading: boolean
  reload: () => void
}

export const useStaffShifts = (
  days: moment.Moment[],
  closedWeekdays?: ReadonlySet<string>,
): UseStaffShiftsResult => {
  const useMocks = getEnv('REACT_APP_USE_SETTINGS_MOCKS', '') === 'true'
  const [staffs, setStaffs] = useState<Provider[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const res = await getAllStaffs()
      if (res.statusCode === 200 && res.data?.staffs?.length) {
        setStaffs(res.data.staffs)
      } else {
        setStaffs(useMocks ? MOCK_PROVIDERS : [])
      }
    } catch {
      setStaffs(useMocks ? MOCK_PROVIDERS : [])
    } finally {
      setLoading(false)
    }
  }, [useMocks])

  useSettingsSubpageLoading(loading)

  useEffect(() => {
    void load()
  }, [load])

  const staffWeeks = useMemo(
    () => staffs.map((s) => toStaffWeek(s, days, closedWeekdays)),
    [staffs, days, closedWeekdays],
  )

  return { staffs, staffWeeks, loading, reload: load }
}
